import { CellSource, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, formatSeconds } from "@reporting/rp-app-host";
import { JobHistory } from "@reporting/rp-types/jobs";

const runStatusColorMap: Record<string, string> = {
	'success': '#018410ff',
	'error': '#e20000ff',
}

const runStatusIconMap: Record<string, string> = {
	'success': 'check-circle',
	'error': 'circle-exclamation',
}

export class JobHistoryGridAdapter extends GridEditorAdapter {

	private _columns?: ColumnSource[];

	constructor(
		private jobName: string,
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;

		this._columns = [{
			data: '_run_status',
			header: msg('Status'),
			t: 't',
			filter: false,
			w: 80,
		}, {
			data: '_run_date',
			header: msg('Date'),
			t: 'dt',
			w: 150
		}, {
			data: 'step_id',
			header: msg('Step ID'),
			t: 'n',
			w: 80,
		}, {
			data: 'step_name',
			header: msg('Step name'),
			t: 't',
			w: 80,
		}, {
			data: '_run_duration',
			header: msg('Duration'),
			t: 'n',
			w: 90,
		}, {
			data: 'message',
			header: msg('Message'),
			t: 't',
			w: 'auto',
		}, {
			data: 'retries_attempted',
			header: msg('Retries attempted'),
			t: 'n',
			w: 150
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const history = await rp.ms('jobs').get<JobHistory[]>('history', { job_name: this.jobName });
		return history.map(data => ({ data: {
			...data,
			_run_status: this.createRunStatusCell(data.run_status),
			_run_date: this.formatDateTime(data.run_date, data.run_time),
			_run_duration: this.createRunDurationCell(data.run_duration),
		}}));
	}

	createRunDurationCell(runDuration: number): Partial<CellSource> {
		// runDuration is in HHMMSS format (as in msdb) - the value stays in seconds so that sorting and filtering work
		if (runDuration == null) return { v: null };
		const seconds = 3600 * Math.floor(runDuration / 10000) + 60 * (Math.floor(runDuration / 100) % 100) + runDuration % 100;
		return { v: seconds, dv: formatSeconds(seconds) };
	}

	createRunStatusCell(run_status: number): Partial<CellSource> {
		return {
			t: 'h',
			v: `<span style="color: ${runStatusColorMap[run_status === 0 ? 'error' : 'success']}">
					<og-icon icon="${runStatusIconMap[run_status === 0 ? 'error' : 'success']}"></og-icon>
					${msg(run_status === 0 ? 'Error' : 'Success')}
				</span>`
		}
	}

	formatDateTime(runDate: number, runTime: number): string {
		// runDate is in YYYYMMDD format, runTime is in HHMMSS format - convert to format YYYY-MM-DD HH:MM:SS
		const dateStr = runDate.toString();
		const timeStr = runTime.toString().padStart(6, '0');
		return `${dateStr.substring(0,4)}-${dateStr.substring(4,6)}-${dateStr.substring(6,8)} ${timeStr.substring(0,2)}:${timeStr.substring(2,4)}:${timeStr.substring(4,6)}`;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: true,
			filters: true,
			columnSorting: true,
			pageSize: null,
			editable: false,
			toolbar: {
				save: {enabled: false},
				discard: {enabled: false},
				new: {enabled: false},
			},
		});
	}

}
